import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowRight, ArrowUpDown, CalendarDays, ClipboardCheck, FileSpreadsheet, FileText, Fuel, RefreshCw, Wrench, type LucideIcon } from 'lucide-react'
import api from '../services/api'
import AnaliseMediaDrawer from '../components/AnaliseMediaDrawer'
import { Medidor, TanqueVisual, corNivel } from './SaldoTanques'

// Painel inicial do Combustível: situação dos tanques, médias abaixo da prevista e pendências do CTA.

interface Tanque {
  item: number; nome: string; capacidade: number; saldo: number; percentual: number; valor: number
  diasAutonomia: number | null; consumoPeriodo: number; consumoOntem: number; consumoHoje: number
}
interface MediaDivergente {
  veicod: string; placa: string; tipo: string; media: number; prevista: number; diferenca: number; km: number; litros: number
  motorista: string; ultimoAbast: { data: string; hora: number } | null; emAnalise: boolean
}
interface Pendencia { num: number; data: string | null; hora: number; placa: string; motorista: string; litros: number; posto: string; tipos: string[] }
interface GrupoAnalise {
  total: number; veiculos: number; porMotivo: { motivo: string; qtd: number; pct: number }[]
  historico: { id: number; quando: string; veicod: string; placa: string; motivo: string; campo: string | null; valor_antes: string | null
    valor_depois: string | null; observacao: string | null; usuario: string; motorista: string | null; data_abast: string | null }[]
}
interface ResumoAnalises { tabelaCriada: boolean; corrigidos: GrupoAnalise; confirmados: GrupoAnalise }
interface Painel {
  tanques: Tanque[]
  medias: { divergentes: MediaDivergente[]; total: number; acima: number; criticas: number; muitoAcima: number; veiculosComMedia: number; analisados: number }
  importacao: {
    total: number; pendencias: Pendencia[]; porTipo: { tipo: string; qtd: number }[]
    errosPeriodo: number | null; historicoDesde: string | null
  }
  encerrantes?: { divergencias: number | null }
  atualizadoEm: string
}

// Faixas do card "Distribuição das divergências de média" (diferenca é % sobre a prevista)
type Faixa = 'muito-abaixo' | 'abaixo' | 'acima' | 'muito-acima'
const FAIXAS: { id: Faixa; seta: string; rotulo: string; intervalo: string; cor: string; fundo: string; borda: string; dentro: (d: number) => boolean }[] = [
  { id: 'muito-abaixo', seta: '↓↓', rotulo: 'Muito abaixo', intervalo: '≤ -20%', cor: '#c91e36', fundo: '#fff0f2', borda: '#ffd4da', dentro: d => d <= -20 },
  { id: 'abaixo', seta: '↓', rotulo: 'Abaixo', intervalo: '-0,01% a -19,99%', cor: '#dc4053', fundo: '#fff7f8', borda: '#ffe1e5', dentro: d => d < 0 && d > -20 },
  { id: 'acima', seta: '↑', rotulo: 'Acima', intervalo: '+0,01% a +19,99%', cor: '#188c4d', fundo: '#f1fbf5', borda: '#d8f1e2', dentro: d => d > 0 && d < 20 },
  { id: 'muito-acima', seta: '↑↑', rotulo: 'Muito acima', intervalo: '≥ +20%', cor: '#087b3e', fundo: '#e8f8ef', borda: '#c7ead5', dentro: d => d >= 20 },
]

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
// Atalhos de período do cabeçalho (o backend limita a 92 dias)
const ATALHOS: { rotulo: string; faixa: () => [string, string] }[] = [
  { rotulo: 'Hoje', faixa: () => { const h = new Date(); return [iso(h), iso(h)] } },
  { rotulo: '7 dias', faixa: () => { const h = new Date(); return [iso(new Date(h.getFullYear(), h.getMonth(), h.getDate() - 6)), iso(h)] } },
  { rotulo: 'Este mês', faixa: () => { const h = new Date(); return [iso(new Date(h.getFullYear(), h.getMonth(), 1)), iso(h)] } },
  { rotulo: 'Mês anterior', faixa: () => { const h = new Date(); return [iso(new Date(h.getFullYear(), h.getMonth() - 1, 1)), iso(new Date(h.getFullYear(), h.getMonth(), 0))] } },
]
const br = (s: string | null) => (s ? s.split('-').reverse().join('/') : '—')
const fmtN = (v: number, c = 0) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c })
const fmtHora = (h: number) => { const [hh, mm = '00'] = h.toFixed(2).split('.'); return `${hh.padStart(2, '0')}:${mm}` }

const secao: React.CSSProperties = {
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 6px 20px -8px rgba(15,23,42,.12)', overflow: 'hidden',
}
const th: React.CSSProperties = { padding: '0.5rem 0.6rem', whiteSpace: 'nowrap', fontSize: '0.68rem' }
const td: React.CSSProperties = { padding: '0.45rem 0.6rem', fontSize: '0.8rem', whiteSpace: 'nowrap' }

function CabecalhoSecao({ icon: Icon, titulo, sub, cor = '#1b3f7b', direita }: { icon: LucideIcon; titulo: string; sub?: string; cor?: string; direita?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem', padding: '0.8rem 1rem', borderBottom: '1px solid #eef2f7' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <span style={{ width: 32, height: 32, borderRadius: 8, background: `${cor}14`, color: cor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={17} /></span>
        <div>
          <div style={{ fontWeight: 800, fontSize: '0.92rem', color: '#153f66' }}>{titulo}</div>
          {sub && <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{sub}</div>}
        </div>
      </div>
      {direita}
    </div>
  )
}

const pilula = (texto: string, cor: string, fundo: string) => (
  <span style={{ padding: '3px 9px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 800, color: cor, background: fundo, whiteSpace: 'nowrap' }}>{texto}</span>
)

// Uma coluna do "Resumo das análises": erros corrigidos (vermelho) ou lançamentos confirmados (verde)
function ColunaAnalise({ tipo, grupo, dataIni, dataFim }: { tipo: 'erro' | 'ok'; grupo: GrupoAnalise; dataIni: string; dataFim: string }) {
  const [verHistorico, setVerHistorico] = useState(false)
  const [menuExportar, setMenuExportar] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erroExportar, setErroExportar] = useState('')
  const erro = tipo === 'erro'
  const cor = erro ? '#cc3041' : '#16884a', fundo = erro ? '#fff2f3' : '#ebf9f1'
  const corBotao = erro ? '#1a6ea8' : '#16884a'

  // Fecha o menu "Exportar" ao clicar fora
  useEffect(() => {
    if (!menuExportar) return
    const fechar = () => setMenuExportar(false)
    document.addEventListener('click', fechar)
    return () => document.removeEventListener('click', fechar)
  }, [menuExportar])

  async function exportar(formato: 'pdf' | 'excel') {
    setMenuExportar(false); setErroExportar(''); setGerando(formato)
    const tipoRel = erro ? 'corrigidos' : 'confirmados'
    try {
      const r = await api.get(`/combustivel/analise-media/resumo/${tipoRel}/${formato}`, { params: { dataIni, dataFim }, responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') window.open(url, '_blank')
      else {
        const a = document.createElement('a'); a.href = url
        a.download = `${erro ? 'erros_corrigidos' : 'medias_confirmadas'}_${dataIni}_${dataFim}.xlsx`
        a.click(); URL.revokeObjectURL(url)
      }
    } catch (e: any) {
      // Erro de validação vem como blob JSON.
      try { const t = await e?.response?.data?.text?.(); setErroExportar(JSON.parse(t).error) } catch { setErroExportar(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.') }
    } finally { setGerando('') }
  }
  return (
    <div style={{ padding: '1rem 1.1rem', borderLeft: erro ? 'none' : '1px solid #eef2f7', minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, paddingBottom: '0.7rem', borderBottom: '1px solid #eef2f7' }}>
        <div>
          <div style={{ fontSize: '0.64rem', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: '#688095' }}>{erro ? 'Erros corrigidos' : 'Lançamentos confirmados'}</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#153f66' }}>{grupo.total} {erro ? (grupo.total === 1 ? 'lançamento' : 'lançamentos') : (grupo.total === 1 ? 'análise correta' : 'análises corretas')}</div>
          <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{grupo.veiculos} {grupo.veiculos === 1 ? 'veículo' : 'veículos'} {erro ? 'com dados ajustados' : 'sem erro no lançamento'}</div>
        </div>
        <span style={{ width: 42, height: 42, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.15rem', fontWeight: 800, background: fundo, color: cor }}>{grupo.total}</span>
      </div>
      {grupo.porMotivo.length === 0 && <div style={{ padding: '0.8rem 0', fontSize: '0.78rem', color: '#94a3b8' }}>Nenhuma análise no período.</div>}
      {grupo.porMotivo.map((m, i) => (
        <div key={m.motivo} style={{ display: 'grid', gridTemplateColumns: '26px 1fr auto', alignItems: 'center', gap: 10, padding: '0.55rem 0', borderBottom: '1px solid #f1f5f9' }}>
          <span style={{ width: 24, height: 24, borderRadius: 7, background: erro ? '#edf4f8' : '#eaf8f0', color: erro ? '#315f7f' : '#16884a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.66rem', fontWeight: 800 }}>{i + 1}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#254761', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={m.motivo}>{m.motivo}</div>
            <div style={{ fontSize: '0.66rem', color: '#64748b' }}>{m.qtd} {m.qtd === 1 ? 'ocorrência' : 'ocorrências'}</div>
          </div>
          <b style={{ fontSize: '0.78rem', color: '#173f60' }}>{fmtN(m.pct, 1)}%</b>
        </div>
      ))}
      {grupo.total > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, paddingTop: '0.7rem' }}>
          {erroExportar && <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#991b1b', marginRight: 'auto' }}>{erroExportar}</span>}
          <button onClick={() => setVerHistorico(v => !v)} style={{ border: `1px solid ${corBotao}`, background: '#fff', color: corBotao, borderRadius: 7, padding: '0.35rem 0.8rem', fontWeight: 800, cursor: 'pointer', fontSize: '0.74rem' }}>
            {verHistorico ? 'Ocultar histórico' : 'Ver histórico'}
          </button>
          <div style={{ position: 'relative' }}>
            <button onClick={e => { e.stopPropagation(); setMenuExportar(v => !v) }} disabled={!!gerando}
              style={{ border: `1px solid ${corBotao}`, background: corBotao, color: '#fff', borderRadius: 7, padding: '0.35rem 0.8rem', fontWeight: 800, cursor: gerando ? 'wait' : 'pointer', fontSize: '0.74rem' }}>
              {gerando ? 'Gerando...' : 'Exportar ▾'}
            </button>
            {menuExportar && (
              <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', right: 0, bottom: 'calc(100% + 6px)', minWidth: 150, padding: 5, background: '#fff', border: '1px solid #dfe7ef', borderRadius: 8, boxShadow: '0 8px 24px rgba(22,54,83,.10)', zIndex: 30 }}>
                <button className="pc-menu-exp" onClick={() => exportar('excel')}><FileSpreadsheet size={14} /> Baixar Excel</button>
                <button className="pc-menu-exp" onClick={() => exportar('pdf')}><FileText size={14} /> Gerar PDF</button>
              </div>
            )}
          </div>
        </div>
      )}
      {verHistorico && (
        <div style={{ marginTop: '0.7rem', border: '1px solid #e2e8f0', borderRadius: 8, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: erro ? 680 : 600 }}>
            <thead><tr>
              <th style={th}>Data da análise</th><th style={th}>Veículo</th><th style={th}>Motorista</th>
              {erro ? <><th style={th}>Erro</th><th style={th}>Antes</th><th style={th}>Depois</th></> : <th style={th}>Motivo</th>}
              <th style={th}>Observação</th><th style={th}>Responsável</th>
            </tr></thead>
            <tbody>
              {grupo.historico.map(h => (
                <tr key={h.id}>
                  <td style={td}>{h.quando}</td>
                  <td style={td}><b>{h.veicod}</b> <span style={{ color: '#94a3b8' }}>{h.placa}</span></td>
                  <td style={td}>{h.motorista || '—'}</td>
                  {erro ? <><td style={td}>{h.motivo}</td><td style={td}>{h.valor_antes}</td><td style={{ ...td, fontWeight: 700, color: '#16884a' }}>{h.valor_depois}</td></> : <td style={{ ...td, whiteSpace: 'normal', minWidth: 180 }}>{h.motivo}</td>}
                  <td style={{ ...td, whiteSpace: 'normal', minWidth: 160, color: '#475569' }}>{h.observacao || '—'}</td>
                  <td style={td}>{h.usuario}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function PainelCombustivel() {
  const navigate = useNavigate()
  const hoje = new Date()
  const [dataIni, setDataIni] = useState(iso(new Date(hoje.getFullYear(), hoje.getMonth(), 1)))
  const [dataFim, setDataFim] = useState(iso(hoje))
  const [dados, setDados] = useState<Painel | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState('')
  const [analisando, setAnalisando] = useState<string | null>(null)
  const [toast, setToast] = useState('')
  const [resumo, setResumo] = useState<ResumoAnalises | null>(null)
  // Filtros da tabela de divergências (iguais ao modelo do usuário)
  const [busca, setBusca] = useState('')
  const [situacao, setSituacao] = useState<'todas' | 'abaixo' | 'acima'>('todas')
  const [desvioMin, setDesvioMin] = useState(10)
  const [ordem, setOrdem] = useState<'desvio' | 'veiculo' | 'motorista'>('desvio')
  const [faixa, setFaixa] = useState<Faixa | null>(null)

  const carregar = useCallback(async (ini = dataIni, fim = dataFim) => {
    setErro(''); setCarregando(true)
    try {
      const [p, r] = await Promise.all([
        api.get('/combustivel/painel', { params: { dataIni: ini, dataFim: fim } }),
        api.get('/combustivel/analise-media/resumo', { params: { dataIni: ini, dataFim: fim } }).catch(() => null),
      ])
      setDados(p.data); setResumo(r?.data ?? null)
    }
    catch (e: any) { setErro(e?.response?.data?.error || 'Erro ao carregar o painel.') }
    finally { setCarregando(false) }
  }, [dataIni, dataFim])

  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(''), 3500); return () => clearTimeout(t) }, [toast])

  // Carrega ao abrir e atualiza sozinho a cada minuto (igual ao Saldo dos Tanques)
  useEffect(() => { carregar() }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setInterval(() => carregar(), 60000); return () => clearInterval(t) }, [carregar])

  const periodoTxt = `${br(dataIni)} a ${br(dataFim)}`
  const irPara = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  const maxTipo = Math.max(1, ...(dados?.importacao.porTipo.map(t => t.qtd) ?? [1]))
  const termo = busca.trim().toLowerCase()
  const divergentes = (dados?.medias.divergentes ?? [])
    .filter(m => (situacao === 'todas' || (situacao === 'abaixo' ? m.diferenca < 0 : m.diferenca > 0))
      && Math.abs(m.diferenca) >= desvioMin
      && (!faixa || FAIXAS.find(f => f.id === faixa)!.dentro(m.diferenca))
      && (!termo || [m.veicod, m.placa, m.motorista].some(c => c.toLowerCase().includes(termo))))
    .sort((a, b) => ordem === 'desvio' ? Math.abs(b.diferenca) - Math.abs(a.diferenca)
      : ordem === 'veiculo' ? a.veicod.localeCompare(b.veicod, undefined, { numeric: true })
      : (a.motorista || '￿').localeCompare(b.motorista || '￿'))

  // Clique na faixa ajusta os filtros da tabela (igual ao modelo) e rola até ela
  const aplicarFaixa = (f: Faixa) => {
    setFaixa(f)
    setSituacao(f.includes('abaixo') ? 'abaixo' : 'acima')
    setDesvioMin(f.startsWith('muito') ? 20 : 0)
    irPara('pc-medias')
  }
  const limparFaixa = () => { setFaixa(null); setSituacao('todas'); setDesvioMin(20) }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', maxWidth: 1500 }}>
      <style>{`@keyframes ondaCombustivel { from { transform: translateX(0) } to { transform: translateX(70px) } } .onda { animation: ondaCombustivel 2.4s linear infinite }
        .pc-link { border: none; background: none; color: #1a6ea8; font-weight: 700; font-size: 0.76rem; cursor: pointer; padding: 0; display: inline-flex; align-items: center; gap: 4px }
        .pc-link:hover { text-decoration: underline }
        .pc-kpi { transition: box-shadow .15s, transform .15s } .pc-kpi:hover { box-shadow: 0 10px 24px -10px rgba(15,23,42,.25); transform: translateY(-1px) }
        .pc-kpis { display: grid; grid-template-columns: minmax(0,1.58fr) minmax(340px,.72fr); gap: 16px; align-items: stretch }
        .pc-dist-bands { display: grid; grid-template-columns: repeat(4,1fr); gap: 10px; padding: 0 17px 17px }
        .pc-dist-band { border: 1px solid transparent; border-radius: 11px; min-height: 112px; padding: 14px 13px; display: grid; grid-template-columns: auto 1fr;
          grid-template-areas: 'arrow count' 'label label' 'range range'; align-items: center; text-align: left; cursor: pointer; transition: .18s; font-family: inherit }
        .pc-dist-band:hover { transform: translateY(-1px); box-shadow: 0 5px 12px rgba(24,58,86,.10) }
        .pc-dist-band.ativa { box-shadow: 0 0 0 2px currentColor inset }
        @media (max-width: 1150px) { .pc-kpis { grid-template-columns: 1fr } }
        @media (max-width: 760px) { .pc-dist-bands { grid-template-columns: repeat(2,1fr) } }
        .pc-menu-exp { display: flex; align-items: center; gap: 7px; width: 100%; border: 0; background: #fff; text-align: left; padding: 8px; border-radius: 5px; color: #294b64; font-size: 0.74rem; font-weight: 700; cursor: pointer }
        .pc-menu-exp:hover { background: #f0f5f8 }
        .pc-gira { animation: pcGira .9s linear infinite } @keyframes pcGira { to { transform: rotate(360deg) } }
        .pc-filtros { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; justify-content: flex-end }
        .pc-atalhos { display: inline-flex; background: #eef2f6; border-radius: 8px; padding: 3px; gap: 2px }
        .pc-atalhos button { border: none; background: none; height: 30px; padding: 0 10px; border-radius: 6px; font-size: 0.76rem; font-weight: 600; color: #475569; cursor: pointer; white-space: nowrap }
        .pc-atalhos button:hover { color: #103d68 }
        .pc-atalhos button.ativo { background: #fff; color: #103d68; box-shadow: 0 1px 3px rgba(15,23,42,.15) }
        .pc-datas { display: inline-flex; align-items: center; gap: 6px; height: 36px; padding: 0 10px; background: #fff; border: 1px solid #cbd5e1; border-radius: 8px }
        .pc-datas:focus-within { border-color: #1a6ea8; box-shadow: 0 0 0 3px rgba(26,110,168,.15) }
        .pc-datas input { border: none !important; outline: none; box-shadow: none !important; background: transparent; height: 32px; width: 118px; padding: 0; font-size: 0.82rem; color: #1e293b }
        .pc-datas span { font-size: 0.76rem; color: #94a3b8 }
        .pc-toolbar { display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap; padding: 0.6rem 1rem; border-bottom: 1px solid #eef2f7; background: #fbfcfd }
        .pc-toolbar label { display: flex; flex-direction: column; gap: 3px; font-size: 0.6rem; font-weight: 800; color: #60778c; text-transform: uppercase }
        .pc-toolbar select { border: 1px solid #d7e1ea; border-radius: 7px; padding: 0 6px; height: 32px; background: #fff; font-size: 0.78rem; text-transform: none; font-weight: 400; color: #1e293b }
        .pc-busca { flex: 1; min-width: 200px; max-width: 310px; height: 32px; border: 1px solid #d7e1ea; border-radius: 7px; padding: 0 10px; font-size: 0.8rem }
        .pc-linha:hover { background: #f5fafe }
        .pc-em-analise { min-width: 88px; padding: 0.25rem 0.75rem; font-size: 0.74rem; font-weight: 700; border-radius: 6px; cursor: pointer; background: #fff4df; color: #a66000; border: 1px solid #e6ab45 }
        .pc-em-analise:hover { background: #ffe9bf; border-color: #d79625 }`}</style>

      {/* Cabeçalho + período */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 800, color: '#103d68' }}>Situação da Frota Hoje</h2>
          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Estoque dos tanques, médias de consumo e pendências de importação em uma tela.</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <div className="pc-filtros">
            <div className="pc-atalhos">
              {ATALHOS.map(a => {
                const [ini, fim] = a.faixa()
                return (
                  <button key={a.rotulo} type="button" className={ini === dataIni && fim === dataFim ? 'ativo' : ''}
                    onClick={() => { setDataIni(ini); setDataFim(fim); carregar(ini, fim) }}>{a.rotulo}</button>
                )
              })}
            </div>
            <div className="pc-datas">
              <CalendarDays size={15} color="#64748b" />
              <input type="date" value={dataIni} max={dataFim} onChange={e => setDataIni(e.target.value)} aria-label="Período inicial" />
              <span>até</span>
              <input type="date" value={dataFim} min={dataIni} onChange={e => setDataFim(e.target.value)} aria-label="Período final" />
            </div>
            <button className="btn btn-primary" onClick={() => carregar()} disabled={carregando}
              style={{ height: 36, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 0.9rem' }}>
              <RefreshCw size={14} className={carregando ? 'pc-gira' : undefined} /> Atualizar
            </button>
          </div>
          <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
            Atualizado em {dados ? new Date(dados.atualizadoEm).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'} · atualiza sozinho a cada minuto
          </div>
        </div>
      </div>

      {erro && <div style={{ fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b' }}>{erro}</div>}
      {!dados && carregando && <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Carregando painel...</div>}

      {/* Alerta de divergência nos encerrantes das bombas (só aparece quando tem) */}
      {!!dados?.encerrantes?.divergencias && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0.75rem 1rem', borderRadius: 10, background: '#fff3f4', border: '1px solid #ffd4da', borderLeft: '5px solid #db3545' }}>
          <AlertTriangle size={20} color="#db3545" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: '0.82rem', color: '#7f1d1d' }}>
            <b>{dados.encerrantes.divergencias} {dados.encerrantes.divergencias === 1 ? 'divergência' : 'divergências'} no encerrante das bombas</b> no período —
            a bomba registrou saída diferente dos abastecimentos do CTA.
          </div>
          <button className="pc-link" style={{ color: '#c62e3d' }}
            onClick={() => navigate(`/combustiveis/saldos/encerrantes?dataIni=${dataIni}&dataFim=${dataFim}&divergencias=1`)}>
            Ver divergências <ArrowRight size={13} />
          </button>
        </div>
      )}

      {dados && (
        <>
          {/* Tanques — mesma animação do Saldo dos Tanques, menor */}
          <div style={secao}>
            <CabecalhoSecao icon={Fuel} titulo="Saldo dos Tanques" sub="Consumo calculado pelo período escolhido • atualiza sozinho a cada minuto"
              direita={<div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {pilula(`Período: ${periodoTxt}`, '#42627a', '#edf4f8')}
                <button className="pc-link" onClick={() => navigate('/combustiveis/saldos')}>Ver detalhes <ArrowRight size={13} /></button>
              </div>} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: '0.9rem', padding: '0.9rem' }}>
              {dados.tanques.map(t => {
                const cor = corNivel(t.percentual)
                const linha = (rotulo: string, valor: string, extra?: React.CSSProperties) => (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: '0.76rem', padding: '3px 0', ...extra }}>
                    <span style={{ color: '#64748b' }}>{rotulo}</span><b style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</b>
                  </div>
                )
                return (
                  <div key={t.item} style={{ border: '1px solid #dfe8ef', borderTop: `3px solid ${cor}`, borderRadius: 10, padding: '0.7rem 0.85rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{ fontWeight: 800, color: '#123c67', fontSize: '0.92rem' }}>{t.nome}</span>
                      <span style={{ fontSize: '0.68rem', color: '#9aabba' }}>Item {t.item}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: 2 }}>
                      <div style={{ flex: 1, textAlign: 'center' }}>
                        <Medidor pct={t.percentual} largura={150} />
                        <div style={{ fontSize: '1.3rem', fontWeight: 900, color: cor, lineHeight: 1 }}>{fmtN(t.percentual, 1)}%</div>
                        <div style={{ fontSize: '0.74rem', color: '#334155', marginTop: 2 }}><b>{fmtN(t.saldo)} L</b> de {fmtN(t.capacidade)} L</div>
                      </div>
                      <TanqueVisual pct={t.percentual} cor={cor} id={`painel-${t.item}`} largura={54} />
                    </div>
                    {t.percentual < 20 && <div style={{ marginTop: 4, fontSize: '0.72rem', fontWeight: 700, color: '#dc2626' }}>⚠ Nível baixo — programar compra</div>}
                    <div style={{ marginTop: '0.5rem', borderTop: '1px dashed #e2e8f0', paddingTop: '0.35rem' }}>
                      {linha('Espaço livre', `${fmtN(Math.max(t.capacidade - t.saldo, 0))} L`)}
                      {linha('Valor em estoque', `R$ ${fmtN(t.valor, 2)}`)}
                      {linha('Autonomia', t.diasAutonomia == null ? '—' : `${fmtN(t.diasAutonomia, 1)} dias`,
                        t.diasAutonomia != null && t.diasAutonomia < 3 ? { color: '#dc2626' } : t.diasAutonomia != null && t.diasAutonomia < 7 ? { color: '#b45309' } : undefined)}
                      <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }} />
                      {linha('Consumido no período', `${fmtN(t.consumoPeriodo)} L`)}
                      {linha('Consumido ontem', `${fmtN(t.consumoOntem)} L`)}
                      {linha('Consumido hoje', `${fmtN(t.consumoHoje)} L`, { background: '#eef8f3', margin: '2px -6px 0', padding: '4px 6px', borderRadius: 6, color: '#16884a' })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Indicadores */}
          <div className="pc-kpis">
            <div style={{ ...secao, borderLeft: '4px solid #315f7f', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '20px 22px 15px' }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#38566f', textTransform: 'uppercase', letterSpacing: '.015em', lineHeight: 1.25 }}>Distribuição das divergências de média</span>
                  <p style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.35, color: '#60788d' }}>Visão geral do período, independentemente dos filtros da tabela</p>
                </div>
                <span style={{ minWidth: 84, height: 72, borderRadius: 15, background: '#eef4f8', color: '#244f6e', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <b style={{ fontSize: 34, lineHeight: .95 }}>{dados.medias.divergentes.length}</b>
                  <small style={{ fontSize: 11, marginTop: 6 }}>veículos</small>
                </span>
              </div>
              <div className="pc-dist-bands">
                {FAIXAS.map(f => (
                  <button key={f.id} className={`pc-dist-band${faixa === f.id ? ' ativa' : ''}`} onClick={() => aplicarFaixa(f.id)}
                    style={{ background: f.fundo, color: f.cor, borderColor: f.borda }}>
                    <span style={{ gridArea: 'arrow', fontSize: 20, lineHeight: 1, fontWeight: 900 }}>{f.seta}</span>
                    <b style={{ gridArea: 'count', textAlign: 'right', fontSize: 34, lineHeight: 1 }}>{dados.medias.divergentes.filter(m => f.dentro(m.diferenca)).length}</b>
                    <strong style={{ gridArea: 'label', fontSize: 14, lineHeight: 1.25, marginTop: 12, fontWeight: 900 }}>{f.rotulo}</strong>
                    <small style={{ gridArea: 'range', fontSize: 11, lineHeight: 1.3, marginTop: 6, fontWeight: 700, opacity: .9 }}>{f.intervalo}</small>
                  </button>
                ))}
              </div>
              <div style={{ marginTop: 'auto', borderTop: '1px solid #dfe7ef', padding: '11px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, color: '#6e8296', fontSize: 11 }}>
                <button className="pc-link" style={{ fontSize: 11, fontWeight: 800 }} onClick={limparFaixa}>Limpar seleção</button>
                <span>{faixa ? (() => { const f = FAIXAS.find(x => x.id === faixa)!; return `Filtro rápido: ${f.rotulo} (${f.intervalo})` })() : 'Clique em uma faixa para filtrar a tabela'}</span>
              </div>
            </div>
            <div className="pc-kpi" style={{ ...secao, padding: '21px 22px', borderLeft: '4px solid #f28b20' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#38566f', textTransform: 'uppercase', lineHeight: 1.25 }}>Erros de importação (CTA)</span>
                <span style={{ width: 46, height: 46, borderRadius: 11, background: '#fff7ec', color: '#f28b20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><AlertTriangle size={20} /></span>
              </div>
              <div style={{ fontSize: 42, fontWeight: 800, color: '#18324c', lineHeight: 1, margin: '23px 0 10px' }}>{dados.importacao.total}</div>
              <div style={{ fontSize: 13, lineHeight: 1.55, color: '#5b7185' }}>
                {dados.importacao.total === 0 ? 'Nenhum abastecimento parado na correção' :
                  dados.importacao.porTipo.filter(t => t.qtd).map(t => `${t.qtd} ${t.tipo.toLowerCase()}`).join(', ')}
              </div>
              <button className="pc-link" style={{ fontSize: 13, paddingTop: 16 }} onClick={() => navigate('/combustiveis/atualizacoes/corrige-importacao')}>Corrigir agora <ArrowRight size={14} /></button>
            </div>
          </div>

          {/* Médias abaixo ou acima da prevista */}
          <div id="pc-medias" style={secao}>
            <CabecalhoSecao icon={ArrowUpDown} cor="#db3545" titulo="Veículos com divergência de média"
              sub="Médias abaixo ou acima da prevista podem indicar inconsistências e devem ser analisadas"
              direita={<div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {pilula(`${dados.medias.total} abaixo`, '#db3545', '#fff3f4')}
                {pilula(`${dados.medias.acima} acima`, '#138a49', '#effbf4')}
                {pilula(`${dados.medias.divergentes.length} pendentes`, '#c66b08', '#fff7ec')}
                {dados.medias.analisados > 0 && pilula(`${dados.medias.analisados} já analisados`, '#42627a', '#edf4f8')}
              </div>} />
            <div className="pc-toolbar">
              <input className="pc-busca" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar veículo, placa ou motorista..." maxLength={60} />
              <label>Situação
                <select value={situacao} onChange={e => { setSituacao(e.target.value as typeof situacao); setFaixa(null) }}>
                  <option value="todas">Todas</option><option value="abaixo">Abaixo da prevista</option><option value="acima">Acima da prevista</option>
                </select>
              </label>
              <label>Desvio mínimo
                <select value={desvioMin} onChange={e => { setDesvioMin(Number(e.target.value)); setFaixa(null) }}>
                  <option value={0}>Todos os desvios</option>
                  {[5, 10, 15, 20, 30].map(v => <option key={v} value={v}>{v}% ou mais</option>)}
                </select>
              </label>
              <label>Ordenação
                <select value={ordem} onChange={e => setOrdem(e.target.value as typeof ordem)}>
                  <option value="desvio">Maior desvio</option><option value="veiculo">Veículo</option><option value="motorista">Motorista</option>
                </select>
              </label>
              <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#64748b', paddingBottom: 6 }}>
                {divergentes.length} {divergentes.length === 1 ? 'veículo exibido' : 'veículos exibidos'}
              </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1050 }}>
                <thead>
                  <tr>
                    <th style={th}>Veículo</th><th style={th}>Placa</th><th style={th}>Motorista</th><th style={th}>Tipo</th><th style={th}>Último abastecimento</th>
                    <th style={{ ...th, textAlign: 'right' }}>Km percorridos</th><th style={{ ...th, textAlign: 'right' }}>Litros</th>
                    <th style={{ ...th, textAlign: 'right' }}>Média</th><th style={{ ...th, textAlign: 'right' }}>Prevista</th>
                    <th style={{ ...th, textAlign: 'right' }}>Diferença</th><th style={th}>Tendência</th><th style={{ ...th, textAlign: 'center' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {divergentes.length === 0 && (
                    <tr><td colSpan={12} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1rem' }}>
                      {dados.medias.divergentes.length === 0 ? 'Nenhum veículo com divergência de média no período. 👍' : 'Nenhum veículo com esses filtros.'}
                    </td></tr>
                  )}
                  {divergentes.map(m => {
                    const acima = m.diferenca > 0
                    const cor = acima ? '#18a957' : '#db3545'
                    return (
                      <tr key={m.veicod} className="pc-linha">
                        <td style={{ ...td, fontWeight: 800 }}>{m.veicod}</td>
                        <td style={td}>{m.placa}</td>
                        <td style={td}>{m.motorista || '—'}</td>
                        <td style={{ ...td, color: '#64748b' }}>{m.tipo}</td>
                        <td style={td}>{m.ultimoAbast ? `${br(m.ultimoAbast.data)} ${fmtHora(m.ultimoAbast.hora)}` : '—'}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{fmtN(m.km)} km</td>
                        <td style={{ ...td, textAlign: 'right' }}>{fmtN(m.litros, 1)} L</td>
                        <td style={{ ...td, textAlign: 'right', color: cor, fontWeight: 800 }}>{fmtN(m.media, 2)} km/L</td>
                        <td style={{ ...td, textAlign: 'right' }}>{fmtN(m.prevista, 2)} km/L</td>
                        <td style={{ ...td, textAlign: 'right', color: cor, fontWeight: 800 }}>{acima ? '+' : ''}{fmtN(m.diferenca, 1)}%</td>
                        <td style={td}>{acima ? pilula('↑ Acima', '#18a957', '#effbf4') : pilula('↓ Abaixo', '#db3545', '#fff3f4')}</td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          {m.emAnalise ? (
                            <button className="pc-em-analise" title="Aguardando retorno do motorista — clique para continuar a análise" onClick={() => setAnalisando(m.veicod)}>Em análise</button>
                          ) : (
                            <button className="btn btn-primary" style={{ padding: '0.25rem 0.75rem', fontSize: '0.74rem', fontWeight: 700, minWidth: 88 }}
                              title="Iniciar análise" onClick={() => setAnalisando(m.veicod)}>Analisar</button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ textAlign: 'center', padding: '0.6rem', fontSize: '0.74rem', color: '#64748b', borderTop: '1px solid #eef2f7' }}>
              O filtro considera o valor absoluto do desvio: por exemplo, 10% inclui valores ≤ -10% e ≥ +10%. •{' '}
              <button className="pc-link" onClick={() => navigate('/combustiveis/relatorios/media-combustivel')}>Abrir relatório Média de Combustível <ArrowRight size={13} /></button>
            </div>
          </div>

          {/* Pendências de importação + resumo por tipo */}
          <div className="pc-duas" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)', gap: '0.9rem' }}>
            <div style={secao}>
              <CabecalhoSecao icon={AlertTriangle} cor="#f28b20" titulo="Pendências de importação" sub="Abastecimentos do CTA parados na correção (ainda não gravados)"
                direita={pilula(`${dados.importacao.total} pendentes`, '#c66b08', '#fff7ec')} />
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                  <thead><tr><th style={th}>Data</th><th style={th}>Placa</th><th style={th}>Posto</th><th style={{ ...th, textAlign: 'right' }}>Litros</th><th style={th}>Erro</th></tr></thead>
                  <tbody>
                    {dados.importacao.pendencias.length === 0 && (
                      <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1rem' }}>Nenhuma pendência de importação no período. 👍</td></tr>
                    )}
                    {dados.importacao.pendencias.map(p => (
                      <tr key={p.num}>
                        <td style={td}>{p.data ? `${br(p.data)} ${fmtHora(p.hora)}` : 'sem data'}</td>
                        <td style={{ ...td, fontWeight: 700 }}>{p.placa || '—'}</td>
                        <td style={td}>{p.posto}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{fmtN(p.litros, 3)}</td>
                        <td style={td}>{p.tipos.map(t => <span key={t} style={{ marginRight: 4 }}>{pilula(t, '#c92736', '#fff0f1')}</span>)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {dados.importacao.total > 0 && (
                <div style={{ textAlign: 'center', padding: '0.6rem', borderTop: '1px solid #eef2f7' }}>
                  <button className="pc-link" onClick={() => navigate('/combustiveis/atualizacoes/corrige-importacao')}><Wrench size={13} /> Corrigir na tela Corrige Lançamentos por Importação</button>
                </div>
              )}
            </div>

            <div style={secao}>
              <CabecalhoSecao icon={AlertTriangle} cor="#f28b20" titulo="Resumo das importações"
                sub={dados.importacao.errosPeriodo == null ? 'Pendências por tipo de erro' : `${dados.importacao.errosPeriodo} erro(s) no período, por tipo`} />
              <div style={{ padding: '0.9rem 1rem' }}>
                {dados.importacao.porTipo.map(t => (
                  <div key={t.tipo} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 28px', gap: 10, alignItems: 'center', margin: '0.55rem 0', fontSize: '0.78rem' }}>
                    <span style={{ color: '#475569' }}>{t.tipo}</span>
                    <div style={{ height: 8, background: '#edf1f4', borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ width: `${(t.qtd / maxTipo) * 100}%`, height: '100%', background: '#f28b20' }} />
                    </div>
                    <b style={{ textAlign: 'right' }}>{t.qtd}</b>
                  </div>
                ))}
                {dados.importacao.historicoDesde && (
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 8 }}>Histórico de erros registrado desde {br(dados.importacao.historicoDesde)}</div>
                )}
              </div>
            </div>
          </div>
          {/* Resumo das análises concluídas */}
          {resumo && (
            <div style={secao}>
              <CabecalhoSecao icon={ClipboardCheck} titulo="Resumo das análises concluídas" sub="Consolidação das análises feitas no período"
                direita={pilula(`Período: ${periodoTxt}`, '#5e7488', '#f0f5f8')} />
              {!resumo.tabelaCriada ? (
                <div style={{ padding: '1rem', fontSize: '0.8rem', color: '#92400e', background: '#fffbeb' }}>
                  A tabela de análises ainda não foi criada nesta base — o botão Analisar não consegue gravar até ela existir.
                </div>
              ) : (
                <div className="pc-duas" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  <ColunaAnalise tipo="erro" grupo={resumo.corrigidos} dataIni={dataIni} dataFim={dataFim} />
                  <ColunaAnalise tipo="ok" grupo={resumo.confirmados} dataIni={dataIni} dataFim={dataFim} />
                </div>
              )}
            </div>
          )}
        </>
      )}
      <style>{`@media (max-width: 1000px) { .pc-duas { grid-template-columns: 1fr !important } }`}</style>

      {analisando && (
        <AnaliseMediaDrawer veicod={analisando} dataIni={dataIni} dataFim={dataFim}
          onFechar={() => setAnalisando(null)}
          onAbrirLancamentos={() => navigate(`/combustiveis/atualizacoes/lancamentos-veiculo?veiculo=${encodeURIComponent(analisando)}&ini=${dataIni}&fim=${dataFim}`)}
          onConcluido={msg => { setAnalisando(null); setToast(msg); carregar() }} />
      )}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#173d5e', color: '#fff', padding: '0.8rem 1.1rem', borderRadius: 9, boxShadow: '0 8px 24px rgba(22,54,83,.25)', zIndex: 70, fontSize: '0.84rem' }}>
          ✓ {toast}
        </div>
      )}
    </div>
  )
}
